import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { Licensing_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GrantAccessDialog,
  GrantAccessSuccessDialog,
} from "../../../components";
import {
  ASSIGNED_VIA_DIRECT,
  AssignedViaCell,
} from "../../../components/AssignedViaCell";
import { DataTable } from "../../../components/data-table/DataTable";
import { DeleteActionCell } from "../../../components/data-table/DeleteActionCell";
import {
  AssignedVia,
  ManageAccessDialog,
} from "../../../components/ManageAccessDialog";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import {
  EXTERNAL_EXPERIENCES_QUERY_KEY,
  LICENSE_ALLOCATIONS_QUERY_KEY,
  useExternalExperiences,
} from "../../../hooks/useExternalExperiences";
import { useLocalization } from "../../../hooks/useLocalization";
import { formatDateYMD } from "../../../shared/utils/date";
import { Routes } from "../../../types/NavigationPaths";
import { INCOMING_LICENSES_QUERY_KEY } from "../../experiences/hooks/useIncomingLicenses";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import {
  USER_RELEASE_CHANNELS_QUERY_KEY,
  UserExperienceRow,
} from "../hooks/useUserExperiences";
import { RemoveUserExperienceAccessDialog } from "./RemoveUserExperienceAccess";

interface ExperiencesTabProps {
  experiences?: UserExperienceRow[];
  userId: string;
}

const DEFAULT_SORT = [{ id: "title", desc: false }];

export const ExperiencesTab: React.FC<ExperiencesTabProps> = ({
  experiences = [],
  userId,
}) => {
  const queryClient = useQueryClient();
  const userManagementService = useUserManagementServiceV1();
  const licensingService = useLicensingServiceV1();
  const { releaseChannelIdToLicenseMap } = useExternalExperiences();
  const { t } = useLocalization();

  const navigate = useNavigate();
  const { companyId } = useParams();

  const handleOnRowClick = (experience: UserExperienceRow) => {
    void navigate(`/companies/${companyId}${Routes.EXPERIENCES}/${experience.id}`);
  };

  const addReleaseChannelMutation = useMutation({
    mutationFn: ({
      releaseChannelId,
      expirationDate,
    }: {
      releaseChannelId: string;
      expirationDate?: Date;
    }) => {
      const releaseChannel = new UserManagement_V1.ReleaseChannel({
        releaseChannelId,
        requirements: UserManagement_V1.ReleaseChannelRequirements.Optional,
        userGroupId: undefined,
        expiresAt: expirationDate,
      });

      return userManagementService.addReleaseChannelToUser(
        userId,
        releaseChannel,
      );
    },
    onSuccess: (_, variables) => {
      const newReleaseChannel = new UserManagement_V1.ReleaseChannel({
        releaseChannelId: variables.releaseChannelId,
        requirements: UserManagement_V1.ReleaseChannelRequirements.Optional,
        userGroupId: undefined,
        expiresAt: variables.expirationDate,
      });

      queryClient.setQueryData(
        [USER_RELEASE_CHANNELS_QUERY_KEY, userId],
        (old: UserManagement_V1.ReleaseChannel[] | undefined) => {
          return old ? [...old, newReleaseChannel] : [newReleaseChannel];
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY, userId],
      });
    },
  });

  const allocateLicenseSeatMutation = useMutation({
    mutationFn: ({
      licenseId,
      expirationDate,
    }: {
      licenseId: string;
      expirationDate?: Date;
      isPinnedToLicenseExpiration: boolean;
      isLimitedSeats: boolean;
    }) => {
      const allocation = new Licensing_V1.LicenseUserAllocationWithoutId({
        userId,
        expiresAt: expirationDate,
        isPinnedToLicenseExpiration: !expirationDate,
        isRevoked: false,
      });

      return licensingService.allocateLicenseUserSeats(licenseId, [allocation]);
    },
    onSuccess: (_, variables) => {
      const licenseExpiration = queryClient
        .getQueryData<Licensing_V1.License[]>([EXTERNAL_EXPERIENCES_QUERY_KEY])
        ?.find((lic) => lic.licenseId === variables.licenseId)?.expiresAt;

      const newAllocation = new Licensing_V1.LicenseUserAllocationWithoutId({
        userId,
        expiresAt: variables.expirationDate ?? licenseExpiration,
        isPinnedToLicenseExpiration: !variables.expirationDate,
        isRevoked: false,
      });

      queryClient.setQueryData(
        [LICENSE_ALLOCATIONS_QUERY_KEY, variables.licenseId],
        (old: Licensing_V1.LicenseUserAllocation[] | undefined) => {
          return old ? [...old, newAllocation] : [newAllocation];
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_ALLOCATIONS_QUERY_KEY, variables.licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
      if (variables.isLimitedSeats) {
        setIsGrantAccessSuccessDialogOpen(true);
      }
    },
  });

  const handleGrantAllocation = async ({
    releaseChannelId,
    expirationDate,
  }: {
    releaseChannelId: string;
    expirationDate?: Date;
  }) => {
    const license = releaseChannelIdToLicenseMap.get(releaseChannelId);

    if (license) {
      await allocateLicenseSeatMutation.mutateAsync({
        licenseId: license.licenseId,
        expirationDate,
        isPinnedToLicenseExpiration: !expirationDate,
        isLimitedSeats: !!license.seats,
      });
    }
  };

  const isSubmitLoading =
    addReleaseChannelMutation.isPending ||
    allocateLicenseSeatMutation.isPending;

  const [selectedExperienceToManage, setSelectedExperienceToManage] =
    useState<UserExperienceRow | null>(null);

  const [isRemoveAccessDialogOpen, setIsRemoveAccessDialogOpen] =
    useState(false);

  const handleCloseRemoveAccessDialog = () => {
    setIsRemoveAccessDialogOpen(false);
    setSelectedExperienceToManage(null);
  };

  const [isGrantAccessDialogOpen, setIsGrantAccessDialogOpen] = useState(false);
  const [isGrantAccessSuccessDialogOpen, setIsGrantAccessSuccessDialogOpen] =
    useState(false);

  const handleOpenGrantAccessDialogOnClose = () => {
    setIsGrantAccessDialogOpen(false);
  };

  const handleOnActionClick = () => {
    setIsGrantAccessDialogOpen(true);
  };

  const [isManageAccessDialogOpen, setIsManageAccessDialogOpen] =
    useState(false);

  const handleOnManageAccessClick = (experience: UserExperienceRow) => {
    setSelectedExperienceToManage(experience);
    setIsManageAccessDialogOpen(true);
  };

  const handleCloseManageAccessDialog = () => {
    setIsManageAccessDialogOpen(false);
    setSelectedExperienceToManage(null);
  };

  const handleOnDirectClick = () => {
    setIsManageAccessDialogOpen(false);
    setIsRemoveAccessDialogOpen(true);
  };

  const hasDirectAccessContentMetadataIds = useMemo(() => {
    const filteredExperiences = experiences.filter((experience) =>
      experience.assignedVia.some(
        (assignedVia) => assignedVia.source === ASSIGNED_VIA_DIRECT,
      ),
    );

    return filteredExperiences.map(
      (experience) => experience.contentMetadataId,
    );
  }, [experiences]);

  const renderTitleCell = (ctx: CellContext<UserExperienceRow, any>) => (
    <span style={{ fontWeight: 500 }}>{ctx.getValue<string>()}</span>
  );

  const renderAssignedViaCell = ({
    getValue,
  }: CellContext<UserExperienceRow, any>) => (
    <AssignedViaCell assignedVia={getValue<AssignedVia[]>()} />
  );

  const renderActionCell = (ctx: CellContext<UserExperienceRow, any>) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const userExperience = ctx.row.original;
      handleOnManageAccessClick(userExperience);
    };

    return (
      <DeleteActionCell
        actionText={t("users.manageAccess")}
        onClick={handleClick}
      />
    );
  };

  const roomMetadataColumns = useMemo<ColumnDef<UserExperienceRow, any>[]>(
    () => [
      {
        id: "title",
        header: t("users.experiencesTableHeaders.title"),
        accessorKey: "title",
        cell: renderTitleCell,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
        size: 180,
      },
      {
        id: "company",
        header: t("users.experiencesTableHeaders.company"),
        accessorKey: "companyName",
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
        size: 150,
        filterFn: "arrIncludesSome",
      },
      {
        id: "Assigned via",
        header: t("users.experiencesTableHeaders.assignedVia"),
        accessorKey: "assignedVia",
        cell: renderAssignedViaCell,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "Expiration Date",
        header: t("users.experiencesTableHeaders.expirationDate"),
        accessorKey: "expirationDate",
        cell: renderExpirationDateCell,
        enableSorting: true,
        enableColumnFilter: false,
        maxSize: 120,
        size: 120,
      },
      {
        id: "actions",
        header: undefined,
        cell: renderActionCell,
        size: 120,
        maxSize: 120,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [t],
  );

  return (
    <>
      <DataTable<UserExperienceRow>
        actionButtonText={t("users.grantAccess")}
        actionButtonTextShort={t("users.grant")}
        columns={roomMetadataColumns}
        data={experiences}
        defaultSort={DEFAULT_SORT}
        facetedColumnIds={["company"]}
        globalFilterPlaceholder={t("users.searchExperiences")}
        onActionClick={handleOnActionClick}
        onRowClick={handleOnRowClick}
      />
      <GrantAccessDialog
        hasAccessContentMetadataIds={hasDirectAccessContentMetadataIds}
        hasAccessText={t("users.directAccess")}
        isOpen={isGrantAccessDialogOpen}
        isSubmitLoading={isSubmitLoading}
        onClose={handleOpenGrantAccessDialogOnClose}
        onGrantAccess={addReleaseChannelMutation.mutateAsync}
        onGrantAllocation={handleGrantAllocation}
      />
      <GrantAccessSuccessDialog
        isOpen={isGrantAccessSuccessDialogOpen}
        onClose={() => setIsGrantAccessSuccessDialogOpen(false)}
      />
      {selectedExperienceToManage && (
        <>
          <ManageAccessDialog
            isOpen={isManageAccessDialogOpen}
            onClose={handleCloseManageAccessDialog}
            onDirectClick={handleOnDirectClick}
            sources={selectedExperienceToManage.assignedVia}
            title={selectedExperienceToManage.title}
          />
          <RemoveUserExperienceAccessDialog
            experience={selectedExperienceToManage}
            isOpen={isRemoveAccessDialogOpen}
            onClose={handleCloseRemoveAccessDialog}
            userId={userId}
          />
        </>
      )}
    </>
  );
};

const renderExpirationDateCell = (ctx: CellContext<UserExperienceRow, any>) => {
  const expirationDate = ctx.getValue<Date | undefined>();

  return formatDateYMD(expirationDate);
};
